import { createStore, useStore } from 'zustand';

import type { components } from '@/lib/api/schema';
import { env } from '@/lib/env';

/**
 * The session, in memory only. The refresh token is an httpOnly cookie the
 * backend sets on /api/v2/auth; the access token never touches storage
 * (backend decision 2026-10-07). A reload starts as `unknown`, and
 * ensureSession() (features/auth/api.ts) turns it into `anonymous` or `signed-in`.
 */
export type User = components['schemas']['UserOut'];
export type SessionStatus = 'unknown' | 'anonymous' | 'signed-in';

export interface SessionState {
  status: SessionStatus;
  accessToken: string | null;
  /** Epoch ms when the access token stops working. */
  expiresAt: number | null;
  user: User | null;
  /** The org of the current URL; becomes the X-Org-Id header. */
  orgId: string | null;
  roleId: string | null;
}

const EMPTY: SessionState = {
  status: 'unknown',
  accessToken: null,
  expiresAt: null,
  user: null,
  orgId: null,
  roleId: null,
};

export const sessionStore = createStore<SessionState>(() => ({ ...EMPTY }));

let refreshing: Promise<string | null> | null = null;

interface RefreshResponse {
  access_token: string;
  expires_in: number;
}

async function requestRefresh(): Promise<string | null> {
  try {
    const res = await fetch(`${env.VITE_API_BASE}/api/v2/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return null;
    const body = (await res.json()) as RefreshResponse;
    sessionStore.setState({ accessToken: body.access_token, expiresAt: Date.now() + body.expires_in * 1000 });
    return body.access_token;
  } catch {
    return null;
  }
}

/**
 * The org an admin or agency user switched to, kept per browser tab so two tabs can
 * show two brands. Brand users never write it: their org comes from the token.
 * The org is not in the URL.
 */
const ACTIVE_ORG_KEY = 'clevrr.activeOrg';
export const activeOrg = {
  read(): string | null {
    try {
      return sessionStorage.getItem(ACTIVE_ORG_KEY);
    } catch {
      return null;
    }
  },
  write(orgId: string | null): void {
    try {
      if (orgId) sessionStorage.setItem(ACTIVE_ORG_KEY, orgId);
      else sessionStorage.removeItem(ACTIVE_ORG_KEY);
    } catch {
      // Storage blocked: the switch lasts until the next reload.
    }
  },
};

/** True for the users who can switch org (admin or agency); the picked org is in `activeOrg`. */
export function canSwitchOrg(user: Pick<User, 'is_admin' | 'agency_id'>): boolean {
  return user.is_admin || Boolean(user.agency_id);
}

/** The org a signed-in page works on: the one picked in this tab for a switcher, else the user's own. */
export function pickOrgId(user: User): string {
  return (canSwitchOrg(user) ? activeOrg.read() : null) ?? user.organization_id;
}

export const session = {
  get(): Readonly<SessionState> {
    return sessionStore.getState();
  },
  set(patch: Partial<SessionState>): void {
    sessionStore.setState(patch);
  },
  /** Forget the user. The cookie, if any, is cleared by POST /auth/logout. */
  clear(): void {
    activeOrg.write(null);
    sessionStore.setState({ ...EMPTY, status: 'anonymous' });
  },
  /** True when the token is missing or within 30 s of expiry. */
  isStale(): boolean {
    const { accessToken, expiresAt } = sessionStore.getState();
    return !accessToken || (expiresAt !== null && Date.now() > expiresAt - 30_000);
  },
  /** Single-flight refresh: concurrent callers share one request. */
  refresh(): Promise<string | null> {
    refreshing ??= requestRefresh().finally(() => {
      refreshing = null;
    });
    return refreshing;
  },
};

export function useSession(): SessionState {
  return useStore(sessionStore);
}

export function useUser(): User | null {
  return useStore(sessionStore, (s) => s.user);
}

/** Display name: "Priya N." style, falling back to the email. */
export function userDisplayName(user: Pick<User, 'first_name' | 'last_name' | 'email'>): string {
  const first = user.first_name?.trim() ?? '';
  const last = user.last_name?.trim() ?? '';
  if (first && last) return `${first} ${last.charAt(0)}.`;
  return first || user.email;
}

export function userInitials(user: Pick<User, 'first_name' | 'last_name' | 'email'>): string {
  const a = user.first_name?.trim().charAt(0) ?? '';
  const b = user.last_name?.trim().charAt(0) ?? '';
  const s = (a + b || user.email.slice(0, 2)).toUpperCase();
  return s;
}
